import { useEffect, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { createDecisionRule, listDecisionRules } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { DecisionRuleView, DecisionView } from '../../../types/cartridgeStudio'

export interface DecisionTableGridProps {
  decision: DecisionView
  onRuleAdded?: () => void
}

/**
 * Design doc §4.4 — `DecisionRuleView` rows for a `kind=table` Decision. `conditions`/`conclusions`
 * are raw JSON objects of DMN unary-test/literal-expression cell text (`CreateDecisionRuleRequest`'s
 * real contract, no structured builder in v66 — a JSON `<textarea>` with inline "must be valid
 * JSON" validation is honest rather than pretending a friendlier editor exists). `order` duplicate
 * under the same decision is a real `409`, surfaced inline naming the conflict.
 */
export function DecisionTableGrid({ decision, onRuleAdded }: DecisionTableGridProps) {
  const [rules, setRules] = useState<DecisionRuleView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conditions, setConditions] = useState('{}')
  const [conclusions, setConclusions] = useState('{"verdict":"pass"}')
  const [order, setOrder] = useState('1')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    // See ProgramWorkspaceScreen's effect for why the `controller.signal.aborted` guard is needed
    // (React 18 StrictMode double-invoke race).
    const controller = new AbortController()
    setError(null)
    listDecisionRules(decision.id, controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return
        setRules(list)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load rules for this Decision.')
      })
    return () => controller.abort()
  }, [decision.id])

  let parsedConditions: unknown = null
  let parsedConclusions: unknown = null
  let jsonError = false
  try {
    parsedConditions = JSON.parse(conditions)
    parsedConclusions = JSON.parse(conclusions)
  } catch {
    jsonError = true
  }
  const parsedOrder = Number(order)
  const isValid = !jsonError && Number.isInteger(parsedOrder)

  async function handleAdd() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createDecisionRule(decision.id, { conditions: parsedConditions, conclusions: parsedConclusions, order: parsedOrder })
      setRules((prev) => [...(prev ?? []), created])
      setOrder(String(parsedOrder + 1))
      onRuleAdded?.()
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not add this rule.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="iw-decision-table">
      <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        Hit policy: <code>{decision.hitPolicy}</code> (read-only here — set when the Decision was created)
      </p>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      <table role="grid" aria-label={`Decision rules for ${decision.key}`} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--iw-size-sm)' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)' }}>Order</th>
            <th style={{ textAlign: 'left', padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)' }}>Conditions</th>
            <th style={{ textAlign: 'left', padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)' }}>Conclusions</th>
          </tr>
        </thead>
        <tbody>
          {rules?.map((rule) => (
            <tr key={rule.id}>
              <td style={{ padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)' }}>{rule.order}</td>
              <td style={{ padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)', fontFamily: 'var(--iw-font-mono)', fontSize: 'var(--iw-size-xs)' }}>
                {JSON.stringify(rule.conditions)}
              </td>
              <td style={{ padding: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)', fontFamily: 'var(--iw-font-mono)', fontSize: 'var(--iw-size-xs)' }}>
                {JSON.stringify(rule.conclusions)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', marginTop: 'var(--iw-space-3)' }}>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor={`rule-order-${decision.id}`}>
            Order
          </label>
          <input id={`rule-order-${decision.id}`} className="iw-input" type="number" value={order} onChange={(e) => setOrder(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor={`rule-conditions-${decision.id}`}>
            Conditions (JSON)
          </label>
          <textarea
            id={`rule-conditions-${decision.id}`}
            className="iw-input"
            rows={2}
            style={{ fontFamily: 'var(--iw-font-mono)' }}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
          />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor={`rule-conclusions-${decision.id}`}>
            Conclusions (JSON — table Decisions feed the aggregator via a <code>verdict</code> key: <code>pass</code>/<code>fail</code>)
          </label>
          <textarea
            id={`rule-conclusions-${decision.id}`}
            className="iw-input"
            rows={2}
            style={{ fontFamily: 'var(--iw-font-mono)' }}
            value={conclusions}
            onChange={(e) => setConclusions(e.target.value)}
          />
        </div>
        {jsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Conditions/Conclusions must be valid JSON.</span> : null}
        <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleAdd()} disabled={!isValid || submitting}>
          <Icon name="plus" size={13} />
          Add rule
        </button>
      </div>
    </div>
  )
}
